import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchNotifications,
  type FetchNotificationsOptions,
} from '../notifications/notificationFeedClient';
import type { NotificationItem } from '../types/models/Notification';

/**
 * Homepage notification-feed store — Story 76.2a.
 *
 * Backs the *"What needs your attention"* section on the Homepage. Separate
 * from `notificationFeedStore` (which backs the Hub panel with its own filter
 * state) so the two surfaces stay decoupled.
 *
 * Source of truth: the BE — every refresh hits `GET /api/notification?tenantId=...`
 * and replaces `items` wholesale. The Homepage never mutates the list in place
 * from SSE payloads; SSE events trigger a re-fetch (debounced) and the BE
 * response is the only thing that lands in the store.
 *
 * Module Federation rule: import via the absolute path
 * `@helios/shared-domain/stores/homepageNotificationFeedStore` (never relative)
 * to avoid dual Zustand store instances across the shell/remote boundary.
 */

const HOMEPAGE_LIMIT = 20;
const RELOAD_DEBOUNCE_MS = 200;

export interface HomepageNotificationFeedStore {
  items: NotificationItem[];
  isLoading: boolean;
  error: string | null;
  tenantId: string | null;

  load: (tenantId: string) => Promise<void>;
  /**
   * Coalesces bursts of SSE events into a single re-fetch. No-op when the
   * store has not been seeded yet (i.e. the Homepage is not mounted).
   */
  scheduleReload: () => void;
  reset: () => void;
}

const initialState = {
  items: [] as NotificationItem[],
  isLoading: false,
  error: null as string | null,
  tenantId: null as string | null,
};

export const useHomepageNotificationFeedStore = create<HomepageNotificationFeedStore>()(
  devtools(
    (set, get) => {
      // Encapsulated inside the store-creator closure so the timer can't be
      // touched from outside this file and is reset together with the store
      // when the module is re-evaluated (HMR safety).
      let reloadTimer: ReturnType<typeof setTimeout> | null = null;

      return {
        ...initialState,

        load: async (tenantId: string) => {
          set(
            {
              isLoading: true,
              error: null,
              tenantId,
            },
            false,
            'homepageNotificationFeed/load/start'
          );
          try {
            const fetchOpts: FetchNotificationsOptions = {
              limit: HOMEPAGE_LIMIT,
              tenantId,
            };
            const res = await fetchNotifications(fetchOpts);
            if (get().tenantId !== tenantId) {
              // Tenant changed mid-flight — discard stale response.
              return;
            }
            set(
              {
                items: res.items,
                isLoading: false,
                error: null,
              },
              false,
              'homepageNotificationFeed/load/success'
            );
          } catch (err) {
            if (get().tenantId !== tenantId) return;
            set(
              {
                isLoading: false,
                error: err instanceof Error ? err.message : 'Failed to load notifications',
              },
              false,
              'homepageNotificationFeed/load/error'
            );
          }
        },

        scheduleReload: () => {
          const { tenantId } = get();
          if (!tenantId) return;
          if (reloadTimer !== null) clearTimeout(reloadTimer);
          reloadTimer = setTimeout(() => {
            reloadTimer = null;
            const current = get().tenantId;
            if (!current) return;
            void get().load(current);
          }, RELOAD_DEBOUNCE_MS);
        },

        reset: () => {
          if (reloadTimer !== null) {
            clearTimeout(reloadTimer);
            reloadTimer = null;
          }
          set({ ...initialState }, false, 'homepageNotificationFeed/reset');
        },
      };
    },
    { name: 'homepageNotificationFeedStore' }
  )
);
